import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useState, useMemo, useEffect, useRef } from "react";
import { DateBar } from "@/v2/components/DateBar";
import { EntryCard } from "@/v2/components/EntryCard";
import { cn } from "@/v2/lib/utils";
import { useDateParams } from "@/v2/hooks/useDateParams";
import { useRubberBand } from "@/v2/hooks/useRubberBand";
import { useData } from "@/v2/contexts/DataProvider";
import { FilterPopover, applyFilters, DEFAULT_FILTER, type FilterState } from "@/v2/components/FilterPopover";
import { SortPopover, sortAndGroup, DEFAULT_SORT, DEFAULT_GROUP, type SortMode, type GroupMode } from "@/v2/components/SortPopover";
import { parseEntryDate } from "@/v2/lib/dates";
import { useMattersCache, useRestrictedWords } from "@/shared/cache";

export default function Exported() {
	useDocumentTitle("Exported");
	const { date, dateRange, setDate, setDateRange } = useDateParams();
	const { exportedEntries, isLoading } = useData();
	const { matters } = useMattersCache();
	const { getRestrictedWords } = useRestrictedWords();

	// Filter & sort state
	const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
	const [sort, setSort] = useState<SortMode>(DEFAULT_SORT);
	const [group, setGroup] = useState<GroupMode>(DEFAULT_GROUP);

	const pageRef = useRef<HTMLDivElement>(null);
	const hasInitiallyScrolled = useRef(false);

	// Bouncy overscroll
	useRubberBand(pageRef);

	// Filter entries by selected date or range
	const dateFilteredEntries = useMemo(() => {
		return exportedEntries.filter((e) => {
			const d = parseEntryDate(e.date);
			if (dateRange.from && dateRange.to) {
				const start = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
				const end = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate(), 23, 59, 59, 999);
				return d >= start && d <= end;
			}
			return (
				d.getFullYear() === date.getFullYear() &&
				d.getMonth() === date.getMonth() &&
				d.getDate() === date.getDate()
			);
		});
	}, [exportedEntries, date, dateRange]);

	// Apply user filters (app, status)
	const filteredEntries = useMemo(
		() => applyFilters(dateFilteredEntries, filter, matters, getRestrictedWords),
		[dateFilteredEntries, filter, matters, getRestrictedWords],
	);

	const isMultiDate = !!(dateRange.from && dateRange.to);
	const groupedEntries = useMemo(
		() => sortAndGroup(filteredEntries, sort, group, isMultiDate),
		[filteredEntries, sort, group, isMultiDate],
	);
	const groupKeys = Object.keys(groupedEntries);

	// Auto-scroll: to top on date change
	useEffect(() => {
		if (!pageRef.current || filteredEntries.length === 0) return;
		if (!hasInitiallyScrolled.current) {
			pageRef.current.scrollTop = 0;
			hasInitiallyScrolled.current = true;
		}
	}, [filteredEntries.length]);

	useEffect(() => {
		hasInitiallyScrolled.current = false;
	}, [date, dateRange]);

	return (
		<div ref={pageRef} className="relative flex flex-col min-h-full">
			{/* Date bar */}
			<div className="sticky top-0 z-20 bg-background border-b border-border">
				<DateBar
					date={date}
					dateRange={dateRange}
					onDateChange={setDate}
					onDateRangeChange={setDateRange}
					leftSlot={
						<FilterPopover
							filter={filter}
							onChange={setFilter}
							entries={dateFilteredEntries}
							matters={matters}
							getRestrictedWords={getRestrictedWords}
						/>
					}
					rightSlot={
						<SortPopover
							sort={sort}
							group={group}
							onSortChange={setSort}
							onGroupChange={setGroup}
						/>
					}
				/>
			</div>

			{/* Entry list */}
			<div className="flex-1">
				{isLoading ? (
					<>
						{[
							{ cards: [
								{ matter: "w-40", client: "w-28", lines: 3, codes: true },
								{ matter: "w-32", client: "w-36", lines: 2, codes: true },
								{ matter: "w-24", client: "w-20", lines: 2, codes: false },
							]},
							{ cards: [
								{ matter: "w-36", client: "w-24", lines: 3, codes: true },
								{ matter: "w-28", client: "w-32", lines: 2, codes: false },
							]},
						].map((group, g) => (
							<div key={g}>
								<div className="px-4 py-1.5 border-b border-border">
									<div className="h-3 w-16 rounded bg-muted animate-pulse" />
								</div>
								{group.cards.map((card, i) => (
									<div key={i} className="flex items-start gap-3 px-4 py-3 border-b border-border">
										<div className="size-8 shrink-0 rounded bg-muted animate-pulse" />
										<div className="flex-1 min-w-0">
											<div className="flex items-baseline justify-between gap-2">
												<div className={cn("h-3.5 rounded bg-muted animate-pulse", card.matter)} />
												<div className="h-3 w-14 rounded bg-muted animate-pulse shrink-0" />
											</div>
											<div className="flex items-center justify-between gap-2 mt-1.5">
												<div className={cn("h-3 rounded bg-muted animate-pulse", card.client)} />
												<div className="h-3 w-10 rounded bg-muted animate-pulse shrink-0" />
											</div>
											<div className="space-y-1.5 mt-2">
												<div className="h-3 w-full rounded bg-muted animate-pulse" />
												<div className="h-3 w-full rounded bg-muted animate-pulse" />
												{card.lines >= 3 && <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />}
											</div>
											{card.codes && (
												<div className="flex items-center gap-1.5 mt-2">
													<div className="h-5 w-10 rounded-full bg-muted animate-pulse" />
													<div className="h-5 w-10 rounded-full bg-muted animate-pulse" />
												</div>
											)}
										</div>
									</div>
								))}
							</div>
						))}
					</>
				) : filteredEntries.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-16 text-center px-6 text-muted-foreground gap-3">
						<p className="text-sm">
							{dateFilteredEntries.length > 0 && filteredEntries.length === 0
								? "No entries match the selected filters."
								: dateRange?.from && dateRange?.to && exportedEntries.length > 0
									? "No exported entries for this date range."
									: exportedEntries.length > 0
										? "No exported entries for this date."
										: "No exported entries yet."}
						</p>
						{dateFilteredEntries.length > 0 && filteredEntries.length === 0 && (
							<button
								onClick={() => setFilter(DEFAULT_FILTER)}
								className="text-sm text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
							>
								Reset filters
							</button>
						)}
					</div>
				) : (
					<div>
						{groupKeys.map((groupKey) => (
							<div key={groupKey || "__flat"}>
								{groupKey && (
									<div className="sticky top-0 z-10 bg-secondary/80 backdrop-blur-sm px-4 py-1.5 text-xs font-medium text-muted-foreground border-b border-border flex items-center justify-between">
										<span>{groupKey}</span>
										<span>{groupedEntries[groupKey].reduce((sum, e) => sum + e.duration, 0).toFixed(1)} hrs</span>
									</div>
								)}
								{groupedEntries[groupKey].map((entry) => (
									<EntryCard
										key={entry.id}
										entry={entry}
									/>
								))}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
